/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { ContactDtoModel, ContactMetaModel } from '@well/domain';
import { customRequiredValidation, ErrorModel } from '@irian-ro/shared-components-module';
import { emailRegexPattern } from '@irian-ro/constants';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PersonInputsComponent } from '@well/person-management';
import { SelectItem } from 'primeng/api';
import { FormUtils } from '@irian-ro/util-forms';
import { DropdownUtilsService } from '@irian-ro/util-translate';
import { takeUntil } from 'rxjs/operators';
import { translationSchema } from '@well/translation';

@Component({
  selector: 'well-contact-inputs',
  templateUrl: './contact-inputs.component.html',
  styleUrls: ['./contact-inputs.component.scss'],
})
export class ContactInputsComponent implements OnInit, OnDestroy {
  @Input() displayVisibilityField = true;
  @Input() showOnlyPersonName = false;
  @Input() editMode = false;

  private _contact: ContactDtoModel = null;
  @Input() set contact(value: ContactDtoModel) {
    setTimeout(() => {
      if (value) {
        this._contact = value;
        FormUtils.removeEmpty(value);
        this.form?.patchValue(value, { emitEvent: true });
        if (this._contact.uuid && !this.editMode) {
          this.disablePersonFormGroup();
        }
      }
    }, 0);
  }

  get contact(): ContactDtoModel {
    return this._contact;
  }

  private _meta: ContactMetaModel = null;

  get meta() {
    return this._meta;
  }

  @Input() set meta(data: ContactMetaModel) {
    if (data) {
      this._meta = data;
      this.visibilityTypes = this.dropdownUtilsService.getDropdownTranslations(
        data.visibilityTypes,
        'contactManagement.inputs.visibilityTypes'
      );
      this.contactMethods = this.dropdownUtilsService.getDropdownTranslations(
        data.contactMethods,
        'contactManagement.inputs.contactMethods'
      );

      if (data.phoneNumberRequired) {
        this.phoneNumberFormControl.setValidators([Validators.required, customRequiredValidation('PhoneNumber')]);
        this.phoneNumberFormControl.updateValueAndValidity();

        this.contactMethodFormControl.setValue('TEL');
      } else {
        this.clearValidators();
        this.contactMethodFormControl.enable();
      }
    }
  }

  @Output() save: Subject<ContactDtoModel> = new Subject();

  disabledPersonFormGroup = false;

  visibilityTypes: SelectItem[] = [];
  contactMethods: SelectItem[] = [];
  errors: Array<ErrorModel> = [];
  form: FormGroup | undefined = undefined;

  contactMethodRequired = false;
  emailRequired = false;
  phoneNumberRequired = false;

  private personForm: PersonInputsComponent = null;

  @ViewChild('personForm', { static: false }) set person(personForm: PersonInputsComponent) {
    setTimeout(() => {
      this.personForm = personForm;

      if (this.personForm) {
        this.form?.setControl('personDTO', personForm.form);
      }
    }, 0);
  }

  private unsubscribe: Subject<boolean> = new Subject<boolean>();

  constructor(private dropdownUtilsService: DropdownUtilsService) {
    this.form = this.createForm();
  }

  get contactMethodFormControl(): FormControl {
    return this.form?.get('contactMethod') as FormControl;
  }

  get phoneNumberFormControl(): FormControl {
    return this.form?.get('phoneNumber') as FormControl;
  }

  get emailFormControl(): FormControl {
    return this.form?.get('email') as FormControl;
  }

  get receivesRequestsFormControl(): FormControl {
    return this.form?.get('receivesRequests') as FormControl;
  }

  get visibilityTypeFormControl(): FormControl {
    return this.form?.get('visibilityType') as FormControl;
  }

  ngOnInit() {
    this.subscribeToContactMethodValueChanges();
    this.subscribeToReceiveRequestsValueChanges();
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
  }

  onSubmit() {
    FormUtils.markAllAsTouched(this.form);
    FormUtils.markAllAsDirty(this.form);
    this.form?.updateValueAndValidity({ emitEvent: true });

    if (this.form?.valid) {
      this.save.next(this.form.getRawValue());
    }
  }

  disablePersonFormGroup() {
    this.disabledPersonFormGroup = true;
  }

  private createForm(): FormGroup {
    return new FormGroup({
      uuid: new FormControl(null),
      phoneNumber: new FormControl(null),
      email: new FormControl(null, Validators.pattern(emailRegexPattern)),
      receivesRequests: new FormControl(false),
      businessProfileUuid: new FormControl(null),
      forLegalEntityUuid: new FormControl(null),
      visibilityType: new FormControl(null, customRequiredValidation('Visibility')),
      contactMethod: new FormControl(null),
      personDTO: new FormControl(null, customRequiredValidation('Person')),
    });
  }

  private clearValidators(): void {
    this.phoneNumberFormControl.clearValidators();
    this.phoneNumberFormControl.updateValueAndValidity();
    this.emailFormControl.setValidators(Validators.pattern(emailRegexPattern));
    this.emailFormControl.updateValueAndValidity();
    this.contactMethodFormControl.clearValidators();
    this.contactMethodFormControl.updateValueAndValidity({ emitEvent: false });
    this.contactMethodRequired = false;
    this.emailRequired = false;
    this.phoneNumberRequired = false;
  }

  private subscribeToReceiveRequestsValueChanges(): void {
    this.receivesRequestsFormControl.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((res) => {
      if (!res) {
        this.contactMethodFormControl.setValue(null);
        this.clearValidators();
      } else {
        this.contactMethodFormControl.setValidators(customRequiredValidation('ContactMethod'));
        this.contactMethodFormControl.updateValueAndValidity({ emitEvent: false });
        this.contactMethodRequired = true;
      }
    });
  }

  private subscribeToContactMethodValueChanges(): void {
    this.contactMethodFormControl.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((res) => {
      if (res === 'TEL') {
        this.phoneNumberFormControl.setValidators(customRequiredValidation('PhoneNumber'));
        this.phoneNumberFormControl.updateValueAndValidity();
        this.emailFormControl.setValidators(Validators.pattern(emailRegexPattern));
        this.emailFormControl.updateValueAndValidity();
        this.phoneNumberRequired = true;
        this.emailRequired = false;
      } else if (res === 'EMAIL') {
        this.emailFormControl.addValidators(customRequiredValidation('Email'));
        this.emailFormControl.updateValueAndValidity();
        this.phoneNumberFormControl.clearValidators();
        this.phoneNumberFormControl.updateValueAndValidity();
        this.phoneNumberRequired = false;
        this.emailRequired = true;
      } else {
        this.clearValidators();
      }
    });
  }

  protected readonly translationSchema = translationSchema;
}
